/**
 * InvigilatorMenuExtraTimeModalController
 * 
 * This class is responsible for managing the extra time modal in the invigilator menu.
 * It handles the initialization, validation, and confirmation of extra time adjustments
 * for a candidate during an exam.
 * 
 * @param {Object} $scope - AngularJS scope object.
 * @param {Object} $uibModalInstance - Instance of the UI Bootstrap modal.
 * @param {Object} candidate - Candidate object containing task-related information.
 * @param {Object} taskCountdownService - Service for managing task countdown and extra time.
 * @param {Object} $eventLog - Service for logging events.
 * @param {Object} $translate - Service for translation.
 * @param {Object} IAAPreferencesService - Service for retrieving candidate preferences.
 * @param {Object} UserInfoService - Service for retrieving user information.
 * @param {Object} $bridges - Service for accessing bridge data.
 */
export default class InvigilatorMenuExtraTimeModalController {
    constructor($scope, $uibModalInstance, candidate, taskCountdownService, $eventLog, $translate, IAAPreferencesService, UserInfoService, $bridges) {
        this.$scope = $scope;
        this.$uibModalInstance = $uibModalInstance;
        this.candidate = candidate;
        this.taskCountdownService = taskCountdownService;
        this.$eventLog = $eventLog;
        this.$translate = $translate;
        this.IAAPreferencesService = IAAPreferencesService;
        this.UserInfoService = UserInfoService;
        this.$bridges = $bridges;

        // Retrieve candidate preferences and user information
        this.candidateIAAPreferences = IAAPreferencesService.getPreferences();
        this.candidateModel = UserInfoService.getUserInfo();

        // Initialize old extra time value
        this.oldExtraTime = angular.isDefined(this.candidateIAAPreferences.extraTime) ? this.candidateIAAPreferences.extraTime : 0;

        // Initialize scope variables
        this.$scope.disabledBtns = false;
        this.$scope.extraTime = angular.isDefined(this.candidateIAAPreferences.extraTime) ? this.candidateIAAPreferences.extraTime : 0;

        // Calculate total available milliseconds
        this.totalAvailableMillis = this.getTotalAvailableMillis();

        // Bind methods to the scope
        this.$scope.getTotalTimeAvailableFormatted = this.getTotalTimeAvailableFormatted.bind(this);
        this.$scope.changedExtraTime = this.changedExtraTime.bind(this);
        this.$scope.onConfirm = this.onConfirm.bind(this);

        // Initialize the modal
        this._init();
    }

    /**
     * Calculates the total available milliseconds for the candidate.
     * 
     * @returns {number|null} - Total available milliseconds or null if mode is not recognized.
     */
    getTotalAvailableMillis() {
        if (this.taskCountdownService.getMode() === 'readingTime') {
            return this.candidate.getTaskTime() + this.taskCountdownService.getTotalExtraTimeMS();
        } else if (this.taskCountdownService.getMode() === 'task') {
            return this.taskCountdownService.getTotalAvailableMillis();
        }
        return null;
    }

    /**
     * Converts milliseconds to minutes.
     * 
     * @param {number} millis - Time in milliseconds.
     * @returns {number} - Time in minutes.
     */
    _millisToMins(millis) {
        return parseInt(millis / 60000);
    }

    /**
     * Converts minutes to milliseconds.
     * 
     * @param {number} mins - Time in minutes.
     * @returns {number} - Time in milliseconds.
     */
    _minsToMillis(mins) {
        return parseInt(mins * 60000);
    }

    /**
     * Initializes the modal by setting up the initial values and bindings.
     */
    _init() {
        this.$scope.totalTimeAvailableFormatted = this.getTotalTimeAvailableFormatted();
        if (this.taskCountdownService.getMode() === 'task') {
            this.$scope.timeElapsed = this.candidateModel.getTaskTimeFormatted(this.totalAvailableMillis - this.taskCountdownService.getRemainingMillis());
            this.$scope.timeLeft = this.candidateModel.getTaskTimeFormatted(this.taskCountdownService.getRemainingMillis());
        } else {
            this.$scope.timeElapsed = 0;
            this.$scope.timeLeft = this.candidateModel.getTaskTimeFormatted(this.totalAvailableMillis + this._minsToMillis(this.$scope.extraTime));
        }
    }

    /**
     * Validates the extra time input.
     * Ensures the extra time does not exceed the maximum allowed and is not negative.
     * Also checks if the extra time has changed and sets the validity accordingly.
     */
    _validation() {
        var taskTime = this.candidate.getTaskTime();
        var maxExtraTime = this._millisToMins(taskTime) * 2;
        var timeElapsedMS = this.$bridges.data().get('timeElapsed');

        if (this.$scope.extraTime > maxExtraTime) {
            this.$scope.extraTime = maxExtraTime;
        }
        if (this.$scope.extraTime < 0) {
            this.$scope.extraTime = 0;
        }
        if ((taskTime - timeElapsedMS + this._minsToMillis(this.$scope.extraTime)) < 0) {
            this.$scope.extraTime += 1;
        }
        if (this.$scope.extraTime !== this.oldExtraTime) {
            this.$scope.valid = true;
        } else {
            this.$scope.valid = false;
        }
    }

    /**
     * Formats the total available time including extra time.
     * 
     * @returns {string} - Formatted total available time.
     */
    getTotalTimeAvailableFormatted() {
        if (this.taskCountdownService.isExtraTimeChanged()) {
            return this.candidateModel.getTaskTimeFormatted(this.totalAvailableMillis + this._minsToMillis(this.$scope.extraTime - this.oldExtraTime));
        }
        return this.candidateModel.getTaskTimeFormatted(this.totalAvailableMillis + this._minsToMillis(this.$scope.extraTime));
    }

    /**
     * Handles the change event of the extra time input.
     * Calls the validation method to ensure the input is valid.
     */
    changedExtraTime() {
        this._validation();
    }

    /**
     * Handles the confirmation of the extra time adjustment.
     * Logs the event, updates the task countdown service, and closes the modal.
     */
    onConfirm() {
        this.$scope.disabledBtns = true;

        var timeElapsedMS = this.$bridges.data().get('timeElapsed');
        if (!angular.isDefined(timeElapsedMS)) {
            timeElapsedMS = 0;
        }

        this.candidateIAAPreferences.extraTime = this.$scope.extraTime;
        this.taskCountdownService.setTotalExtraTimeMS(this.$scope.extraTime * 60000, true);

        this.$scope.newTotalTimeAvailable = this.candidate.getTaskTime() - timeElapsedMS + (this.$scope.extraTime * 60000);
        this.$scope.newTotalTimeAvailableFormatted = this.candidateModel.getTaskTimeFormatted(this.$scope.newTotalTimeAvailable);

        var message;
        if (this.$scope.extraTime < this.oldExtraTime) {
            message = this.$translate.instant('MODULES.ADMIN.EVENTLOG_EXTRATIME_DEDUCTED', {
                'deducted':       this.$scope.extraTime < this.oldExtraTime ? this.oldExtraTime - this.$scope.extraTime : 0,
                'minutes':        this.$scope.extraTime,
                'new_total_time': this.$scope.newTotalTimeAvailableFormatted
            });
        } else {
            message = this.$translate.instant('MODULES.ADMIN.EVENTLOG_EXTRATIME_ADD', {
                'add':            this.$scope.extraTime,
                'minutes':        this.$scope.extraTime,
                'new_total_time': this.$scope.newTotalTimeAvailableFormatted
            });
        }

        if (this.$scope.selectedEventTypeReason) {
            this.$eventLog.logEvent(message, "extraTime", this.$scope.eventExplanation, this.$scope.selectedEventTypeReason, {});
        } else {
            this.$eventLog.logEvent(message, "extraTime", this.$scope.eventExplanation, '', {});
        }

        this.$uibModalInstance.close({ confirm: true });
    }
}